/**
 * Login Component
 *
 * This component renders a login form with support for password reset.
 * It includes reusable input and button components for optimization and maintains UI state using props.
 *
 * Props:
 * @param {string} email - The user's email input.
 * @param {Function} setemail - Function to update the email state.
 * @param {string} password - The user's password input.
 * @param {Function} setPassword - Function to update the password state.
 * @param {string} error - Error message to display.
 * @param {boolean} loading - Whether the form is in a loading state.
 * @param {Function} handleLogin - Function to handle the login action.
 * @param {boolean} forgotPassword - Whether the password reset form is displayed.
 * @param {Function} setForgotPassword - Function to toggle the forgot password state.
 * @param {string} resetEmail - The email input for password reset.
 * @param {Function} setResetEmail - Function to update the reset email state.
 * @param {Function} handleForgotPassword - Function to handle the forgot password action.
 */
import React, { memo, useEffect, useState } from "react"
import lockerImg from "../../asset/locker_img.jpeg"
import PasswordEyeButton from "./button/PasswordEyeButton";

interface InputFieldProps {
  label: string
  type: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  disabled: boolean
}

interface ButtonProps {
  onClick: () => void
  text: string
  disabled: boolean
  isPrimary?: boolean
}

interface LoginProps {
  email: string
  setemail: (value: string) => void
  password: string
  setPassword: (value: string) => void
  error: string
  loading: boolean
  handleLogin: () => void
  forgotPassword: boolean
  setForgotPassword: (value: boolean) => void
  resetEmail: string
  setResetEmail: (value: string) => void
  handleForgotPassword: () => void
}

/**
 * Reusable Input Component wrapped with React.memo for optimization.
 */
const InputField: React.FC<InputFieldProps> = memo(
  ({ label, type, value, onChange, disabled }) => {
    const id = label.replace(/\s+/g, "-").toLowerCase() // ✅ Correctly inside function block

    return (
      <>
        <label
          htmlFor={id}
          className="font-semibold text-sm text-gray-600 pb-1 block"
        >
          {label}
        </label>
        <input
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className="border rounded-lg px-3 py-2 mt-1 mb-5 text-sm w-full"
        />
      </>
    )
  }
)

/**
 * Reusable Button Component wrapped with React.memo for optimization.
 */
const Button: React.FC<ButtonProps> = memo(
  ({ onClick, text, disabled, isPrimary = true }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      type="button"
      className={`transition duration-200 ${isPrimary
          ? "bg-blue-500 hover:bg-blue-600 focus:bg-blue-700 focus:ring-blue-500 text-white"
          : "bg-gray-100 hover:bg-gray-200 focus:bg-gray-300 focus:ring-gray-200 text-gray-700"
        } focus:shadow-sm focus:ring-4 focus:ring-opacity-50 w-full py-2.5 rounded-lg text-sm shadow-sm hover:shadow-md font-semibold text-center inline-block`}
    >
      {text}
    </button>
  )
)

const Login: React.FC<LoginProps> = ({
  email,
  setemail,
  password,
  setPassword,
  error,
  loading,
  handleLogin,
  forgotPassword,
  setForgotPassword,
  resetEmail,
  setResetEmail,
  handleForgotPassword,
}) => {

  const [visible, setVisible] = useState(true)

  return (
    <div
      className="min-h-screen bg-gray-100 flex flex-col justify-center sm:py-12"
      style={{
        backgroundImage: `url(${lockerImg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {loading && (
        <div className="absolute inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
          <div className="text-white text-lg font-semibold">Loading....</div>
        </div>
      )}
      <div
        className={`p-10 xs:p-0 mx-auto md:w-full md:max-w-lg ${loading ? "pointer-events-none" : ""
          }`}
      >
        <div className="bg-white shadow w-full rounded-lg divide-y divide-gray-200">
          <div className="px-5 py-7">
            {error && <p className="text-red-500 mb-3">{error}</p>}

            {!forgotPassword ? (
              <>
                <h2 className="text-2xl font-semibold text-gray-600 mb-5 text-center">
                  Login
                </h2>
                <InputField
                  label="email"
                  type="text"
                  value={email}
                  onChange={(e) => setemail(e.target.value)}
                  disabled={loading}
                />
                <InputField
                  label="Password"
                  type={!visible ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                {/* Eye icon on password field */}
                <PasswordEyeButton
                  show={!visible}
                  onToggle={() => setVisible(!visible)}
                  id="password-visibility-toggle"
                />
                <Button
                  onClick={handleLogin}
                  text={loading ? "Loading..." : "Login"}
                  disabled={loading}
                />
                <div className="px-5 py-3 text-center">
                  <button
                    onClick={() => setForgotPassword(true)}
                    disabled={loading}
                    type="button"
                    className="text-sm text-blue-500 hover:underline focus:outline-none"
                  >
                    Forgot Password?
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-semibold text-gray-600 mb-5 text-center">
                  Reset Password
                </h2>
                <InputField
                  label="Enter your email"
                  type="text"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  disabled={loading}
                />
                <Button
                  onClick={handleForgotPassword}
                  text={loading ? "Loading..." : "Send Reset Link"}
                  disabled={loading}
                />
                <div className="px-5 py-3 text-center">
                  <button
                    onClick={() => setForgotPassword(false)}
                    disabled={loading}
                    type="button"
                    className="text-sm text-gray-500 hover:underline focus:outline-none"
                  >
                    Back to Login
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login